import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import UploaderMultiple from 'components/UploaderMultiple';
import HonorariosEntidades from 'components/MatriculateRequest/HonorariosEntidades';
import {
  getAllConsultoriosAsDirector,
  getConsultorioUpdateEdiliciaRequest,
  putConsultorioUpdateEdilicia,
} from '../../features/consultorios/consultoriosAPI';
import {
  ConsultorioCokibaBD,
  ConsultorioUpdateEdilicia,
} from 'types/Consultorio';

export default function EditConsultoriosUpdateEdilicia() {
  const { id } = useParams<any>();
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof ConsultorioUpdateEdilicia, boolean>>
  >({});

  const [disableSend, setDisableSend] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [consultorios, setConsultorios] = useState<ConsultorioCokibaBD[]>([]);

  const [canUpdateByState, setCanUpdateByState] = useState(false);
  const [canUpdateAttachs, setCanUpdateAttachs] = useState(false);

  const [data, setData] = useState<ConsultorioUpdateEdilicia>({
    id: 0,
    state_id: null,
    matriculate_id: null,
    consultorio_id: null,
    numero_gestion: null,
    cantidad_gabinetes: null,
    numero_expediente: null,
    anio_expediente: null,
    numero_habilitacion: null,
    fecha_habilitacion: null,
    created_at: null,
    resolved_at: null,
    deleted_at: null,
    motivo_rechazo: null,
    user_id: null,
    state: null,
    attachs: [],
  });

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        getAllConsultoriosAsDirector()
          .then(response => {
            setConsultorios(response);
          })
          .catch(err => {
            alert(true, err.getMessage() || 'No se puedieron obtener los pedidos del matriculado');
          })
          .finally(() => {
            setLoading(false);
          });

        const updateRequest: ConsultorioUpdateEdilicia =
          await getConsultorioUpdateEdiliciaRequest(id) as ConsultorioUpdateEdilicia;
        if (!!updateRequest) {
          setData({
            ...updateRequest,
          });
        } else {
          throw new Error('No se pudo recuperar la solicitud de consultorio');
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultorioUpdateEdiliciaCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (data.state_id == 3) {
      setCanUpdateByState(false);
      setCanUpdateAttachs(true);
    }
  }, [data.state_id]);

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de consultorio?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const consultorioUpdate: Partial<ConsultorioUpdateEdilicia> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      consultorioUpdate[key as keyof ConsultorioUpdateEdilicia] = value.toString() as any;
    });

    // Seteamos el id
    consultorioUpdate['id'] = data.id;

    // Preparamos los archivos
    const files: Record<string, File> = {};
    const inputs = Array.from(document.querySelectorAll('input[type="file"]'));
    for (const inputElement of inputs) {
      const input = inputElement as HTMLInputElement;
      if (input.hasAttribute('required') && input.value === '') {
        alert(
          'Faltan subir archivos requeridos para poder enviar la solicitud.',
        );
        setLoading(false);
        return;
      }

      if (input.files) {
        for (let i = 0; i < input.files.length; i++) {
          files[`${input.name}_${i}`] = input.files[i];
        }
      }
    }

    // Enviamos la petición
    await putConsultorioUpdateEdilicia(consultorioUpdate, files)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('ConsultoriosUpdateEdiliciaEdit', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/consultorios');
        });
      })
      .catch(err => {
        console.error(
          'ConsultoriosUpdateEdiliciaEdit',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | boolean | null;
    value = target.value;

    if (target.type === 'checkbox') {
      value = target.checked;
    }

    if (
      typeof value === 'string' &&
      ['consultorio_id'].includes(name)
    ) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  return (
    <div className="content">
      { data.motivo_rechazo
        ?
          <Alert color="warning">
            <Row className="mb-2">
              <Col sm={12}>
                <span className='group-name observations'>
                  Motivo de rechazo
                  <p className="mt-2">
                    { data.motivo_rechazo }
                  </p>
                </span>
              </Col>
            </Row>
          </Alert>
        : null
      }
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Editar Actualización Edilicia del Consultorio</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/consultorios')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Consultorio</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      <Label for="consultorio_id">
                        Consultorio <Required />
                      </Label>
                      <Input
                        id="consultorio_id"
                        name="consultorio_id"
                        type="select"
                        className="custom-select form-control"
                        value={data.consultorio_id ?? ''}
                        invalid={formValidation.consultorio_id === false}
                        onChange={handleInputChange}
                        disabled={isLoading || !canUpdateByState}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        {consultorios?.map(item => {
                          return (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.nombre}, {item.calle} {item.numero}, {item.localidad?.MTR008_D_LOCALIDAD}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Adjuntos</CardTitle>
                </Col>
                <Col sm={12}>
                  <HonorariosEntidades
                    titulosPagos={[
                      'TASA_MINISTERIO_CAMBIO_CONSULTORIO',
                      'ARANCEL_INSPECCION_COKIBA',
                      'ARBA_TIMBRADO_EN_BLANCO',
                      'ARBA_TASA_ADMINISTRATIVA',
                    ]}
                  />
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="constancia_pagos">
                        Constancia de haber realizado los 4 pagos detallados anteriormente <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="constancia_pagos"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        multiple
                      />
                    </Col>
                    <Col md="6">
                      <Label for="solicitud_habilitacion">
                        Nota Tipo 7 <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="solicitud_habilitacion"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={['application/pdf']}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="declaracion_aparatologia">
                        Declaración Jurada de aparatología <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="declaracion_aparatologia"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                      />
                    </Col>
                    <Col md="6">
                      <Label for="croquis_memoria">
                        Memoria descriptiva (Describir en una pequeña redacción la distribución de los nuevos espacios.
                        Firmar y sellar. Descargar ejemplo de la web) <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="croquis_memoria"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="plano_municipal">
                        Plano Municipal <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="plano_municipal"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={[
                          'image/jpeg',
                          'image/png',
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                        multiple
                      />
                    </Col>
                    <Col md="6">
                      <Label for="resolucion_ministerio">
                        Resolución Ministerial vigente <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="resolucion_ministerio"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={['application/pdf']}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="contrato_alquiler">
                        Contrato de Alquiler <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="contrato_alquiler"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        multiple
                      />
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                { [3].includes(data.state_id || 0)
                  ?
                    <FormGroup>
                      <div className="text-center">
                        <Button
                          type="button"
                          className="btn-round"
                          onClick={handleSendForm}
                          disabled={isLoading || disableSend}
                        >
                          Enviar
                        </Button>
                      </div>
                    </FormGroup>
                  : null
                }
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
